import { useState, useCallback, useEffect } from "react";

interface VoiceInputState {
  isListening: boolean;
  isProcessingPunctuation: boolean;
}

/**
 * Custom hook for managing shared MediaStream between voice input and audio visualization
 *
 * @param voiceInputState - Current state of voice input (listening/processing)
 * @returns Object with shared audio stream and initialization function
 */
export const useSharedAudioStream = (voiceInputState: VoiceInputState) => {
  const [sharedAudioStream, setSharedAudioStream] = useState<MediaStream | null>(null);

  // Initialize shared microphone stream when voice listening starts
  const initializeSharedAudioStream = useCallback(async (): Promise<MediaStream | null> => {
    if (sharedAudioStream) {
      return sharedAudioStream;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setSharedAudioStream(stream);
      console.log("Initialized shared audio stream");
      return stream;
    } catch (error) {
      console.error("Failed to initialize shared audio stream:", error);
      return null;
    }
  }, [sharedAudioStream]);

  // Cleanup shared audio stream
  const cleanupSharedAudioStream = useCallback(() => {
    if (sharedAudioStream) {
      sharedAudioStream.getTracks().forEach((track) => track.stop());
      setSharedAudioStream(null);
      console.log("Cleaned up shared audio stream");
    }
  }, [sharedAudioStream]);

  // Effect to cleanup shared stream when voice stops listening
  useEffect(() => {
    if (!voiceInputState.isListening && !voiceInputState.isProcessingPunctuation) {
      // Small delay to allow components to finish processing
      const cleanup = setTimeout(() => {
        cleanupSharedAudioStream();
      }, 1000);
      return () => clearTimeout(cleanup);
    }
  }, [
    voiceInputState.isListening,
    voiceInputState.isProcessingPunctuation,
    cleanupSharedAudioStream,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sharedAudioStream) {
        sharedAudioStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [sharedAudioStream]);

  return {
    sharedAudioStream,
    initializeSharedAudioStream,
    cleanupSharedAudioStream,
  };
};
